// src/utils.js

// 카테고리별 가산치 (엑셀 시트 기준)
export const categoryScores = {
  "메이크업": 1.1,
  "스킨케어": 1.2,
  "헤어케어": 0.6,
  "바디케어": 0.8,
  "네일케어": 0.8,
  "향수": 0.5,
  "맨즈케어": 0.4,
};

// 팔로워 수 범위별 multiplier 테이블
const followerMultiplierTable = [
  { threshold: 0, rate: 0.1 },
  { threshold: 1000, rate: 0.3 },
  { threshold: 2000, rate: 0.5 },
  { threshold: 3000, rate: 1 },
  { threshold: 5000, rate: 1.05 },
  { threshold: 10000, rate: 1.1025 },
  { threshold: 50000, rate: 1.157625 },
  { threshold: 100000, rate: 1.21550625 },
  { threshold: 300000, rate: 1.276281563 },
  { threshold: 1000000, rate: 1.340095641 },
  { threshold: 2000000, rate: 1.407100423 },
  { threshold: 5000000, rate: 1.477455444 },
  { threshold: 10000000, rate: 1.551328216 },
];

/**
 * getFollowerMultiplier
 * 주어진 팔로워 수에 대해, 테이블에서 가장 큰 threshold 이하의 rate를 반환합니다.
 */
export function getFollowerMultiplier(followerCount) {
  let multiplier = followerMultiplierTable[0].rate;
  for (let i = 0; i < followerMultiplierTable.length; i++) {
    if (followerCount >= followerMultiplierTable[i].threshold) {
      multiplier = followerMultiplierTable[i].rate;
    } else {
      break;
    }
  }
  return multiplier;
}

// 상수값 (엑셀 시트 기준)
// 팔로워 1인당 연간 기대수익: $0.11
// 연간 기대 콜라보 수: 20회 (최근 콜라보 수는 Home.js에서 20으로 고정)
const BASE_EARNING_PER_FOLLOWER = 0.2;

/**
 * calculateEarningRange
 * 
 * 이번에 "최근 1년 간 콜라보 수 / 캠페인 당 기대 수익" 을 곱하는 로직을 추가했습니다.
 *   - recentCollabs: 최근 1년 간 콜라보 수 (고정: 20)
 *   - campaignCost:  캠페인 당 기대 수익(또는 비용) 예시: 30
 * 
 * 
 * 새 공식:
 *   기본 수익(baseEarning)
 *     = 팔로워 수 × BASE_EARNING_PER_FOLLOWER × (팔로워 multiplier)
 *   조정 수익(adjusted)
 *     = 기본 수익 × (카테고리 가산치) × (최근1년 콜라보 수 / 캠페인 당 기대 수익)
 *   최종 범위
 *     = [조정수익 × 1.0, 조정수익 × 1.1, 조정수익 × 1.2]
 */
export function calculateEarningRange(followerCount, category, recentCollabs, campaignCost) {
  // 1) 팔로워 multiplier
  const followerMultiplier = getFollowerMultiplier(followerCount);

  // 2) 기본 수익
  const baseEarning = followerCount * BASE_EARNING_PER_FOLLOWER * followerMultiplier;

  // 3) 카테고리 가중치
  const categoryScore = categoryScores[category] || 1.0;

  // 4) 최근1년 콜라보 / 캠페인 기대수익
  const collabFactor = recentCollabs / campaignCost;

  // 5) 최종 조정 수익
  const adjusted = baseEarning * categoryScore * collabFactor;

  // 6) 최소, 중앙, 최대
  const minEarning = Math.floor(adjusted * 0.65);
  const midEarning = Math.floor(adjusted * 0.8);
  const maxEarning = Math.floor(adjusted * 1);

  return { minEarning, midEarning, maxEarning };
}

/**
 * computeMonetizationIndex
 * 팔로워-수익화 지수 = (중앙 예상 수익 / 팔로워 수)
 * 
 * - 여기서도 calculateEarningRange를 부를 때, recentCollabs와 campaignCost를 넣어줘야 함
 */
export function computeMonetizationIndex(followerCount, category, recentCollabs, campaignCost) {
  if (followerCount <= 0) return 0;
  const { midEarning } = calculateEarningRange(followerCount, category, recentCollabs, campaignCost);
  return midEarning / followerCount;
}

/**
 * computeTopPercentile
 *
 * 이 함수는 "효과적 팔로워 수(effectiveFollower)"만 계산해서
 * 퍼센타일(상위 몇 %)를 구합니다. 
 * 
 * 수익 계산과 별개로, 가중치만 써서 순위화하는 로직이라
 * "최근 1년 간 콜라보 / 캠페인 비용"을 억지로 넣을 수도 있지만
 * 사용자님이 원치 않으면 기존대로 두어도 됩니다.
 */
export function computeTopPercentile(followerCount, category) {
  console.log("category in computeTopPercentile:", category);
  const categoryScore = categoryScores[category] || 1.0;
  console.log("categoryScore:", categoryScore);
  const followerMultiplier = getFollowerMultiplier(followerCount);
  const effectiveFollower = followerCount * categoryScore * followerMultiplier;
  
  // 여기서 minEffective, maxEffective는 기존과 동일
  const minEffective = 0;
  const maxEffective = 13000;
  
  if (effectiveFollower < minEffective) return 99;
  
  const logMin = Math.log10(minEffective + 1);
  const logMax = Math.log10(maxEffective + 1);
  const logEffective = Math.log10(effectiveFollower + 1);
  
  let percentile = ((logMax - logEffective) / (logMax - logMin)) * 100;
  if (percentile < 1) percentile = 1;
  if (percentile > 99) percentile = 99;
  return Math.floor(percentile);
}

/**
 * getDescription
 * 계산된 상위 퍼센타일에 따라 미리 정의된 제목과 설명을 반환합니다.
 * (이 부분은 기존 로직 그대로)
 */
export function getDescription(topPercentile) {
  if (topPercentile === 1) {
    return {
      title: "Beauty Overlord",
      description: "You’ve climbed to the top like a cosmic phenomenon. You shine so bright, NASA might be tracking your glow. The Beauty Scouter salutes your unstoppable radiance!",
      image: "https://beauty-scouter.s3.us-east-2.amazonaws.com/BeautyOverlord.png",
    };
  } else if (topPercentile >= 2 && topPercentile <= 5) {
    return {
      title: "Beauty Tycoon",
      description: "When you post, museums might call it art. You run the beauty world like a CEO—with vision, power, and fabulous flair.",
      image: "https://beauty-scouter.s3.us-east-2.amazonaws.com/BeautyTycoon.png",
    };
  } else if (topPercentile >= 6 && topPercentile <= 10) {
    return {
      title: "Beauty Titan",
      description: "You stand tall with cosmic brilliance. The Beauty Scouter sees you shining like a supernova—keep dazzling, Titan!",
      image: "https://beauty-scouter.s3.us-east-2.amazonaws.com/BeautyTitan.png",
    };
  } else if (topPercentile >= 11 && topPercentile <= 20) {
    return {
      title: "Beauty Rising Royalty",
      description: "You’ve got main character energy. Like a beloved drama lead, you're stealing the spotlight—and it’s only a matter of time before you claim the crown.",
      image: "https://beauty-scouter.s3.us-east-2.amazonaws.com/BeautyRisingRoyalty.png",
    };
  } else if (topPercentile >= 21 && topPercentile <= 30) {
    return {
      title: "Hidden Gem",
      description: "You’re glowing beneath the surface, just one big break away from your debut. Keep going—there’s a spotlight with your name on it!",
      image: "https://beauty-scouter.s3.us-east-2.amazonaws.com/HiddenGem.png",
    };
  } else if (topPercentile >= 31 && topPercentile <= 40) {
    return {
      title: "Meteor in Orbit",
      description: "You’re floating through the influencer galaxy, just waiting for that final boost to shine bright. Hang in there—your moment’s coming.",
      image: "https://beauty-scouter.s3.us-east-2.amazonaws.com/MeteorinOrbit.png",
    };
  } else if (topPercentile >= 41 && topPercentile <= 50) {
    return {
      title: "Rookie with a Roar",
      description: "You might be the new kid on the block, but your potential is louder than a stadium cheer. Keep growing—everyone loves a rising star!",
      image: "https://beauty-scouter.s3.us-east-2.amazonaws.com/RookiewithaRoar.png",
    };
  } else if (topPercentile >= 51 && topPercentile <= 60) {
    return {
      title: "Unstoppable Trainee",
      description: "This is your training arc, and you’re leveling up fast. Stay consistent—your glow-up is just getting started.",
      image: "https://beauty-scouter.s3.us-east-2.amazonaws.com/UnstoppableTrainee.png",
    };
  } else if (topPercentile >= 61 && topPercentile <= 70) {
    return {
      title: "Spark in the Making",
      description: "Every star starts with a spark. Keep that fire alive—your influence is just waiting to take off.",
      image: "https://beauty-scouter.s3.us-east-2.amazonaws.com/SparkintheMaking.png",
    };
  } else if (topPercentile >= 71 && topPercentile <= 80) {
    return {
      title: "Cheerful Underdog",
      description: "The crowd might not be cheering yet, but your energy is infectious. Keep it up—and they’ll be chanting your name soon enough.",
      image: "https://beauty-scouter.s3.us-east-2.amazonaws.com/CheerfulUnderdog.png",
    };
  } else if (topPercentile >= 81 && topPercentile <= 90) {
    return {
      title: "Tomorrow’s Headliner",
      description: "You're like a band warming up backstage—lowkey but full of potential. Stay on beat, and the spotlight will find you.",
      image: "https://beauty-scouter.s3.us-east-2.amazonaws.com/TomorrowsHeadliner.png",
    };
  } else if (topPercentile >= 91 && topPercentile <= 99) {
    return {
      title: "Courageous Starter",
      description: "You're just getting started, but every great story starts somewhere. Keep moving—you might just turn heads sooner than you think.",
      image: "https://beauty-scouter.s3.us-east-2.amazonaws.com/CourageousStarter.png",
    };
  }
  return { title: "Influencer", description: "Keep shining and growing!" };
}

/**
 * computeExcelFormula
 * (이 부분은 사용여부에 따라 유지)
 */
export function computeExcelFormula(valueC7, lowerBound, upperBound, multiplier) {
  if (valueC7 >= lowerBound && valueC7 < upperBound) {
    return valueC7 * multiplier;
  } else {
    return "";
  }
}
